<script setup lang="ts">
  // Table page command bar: back + table identity (name, open-since, count,
  // session covers chip) + search + actions. Finalizar / + Pedido actions are
  // owned by the page (emits only). Color roles: orange = create (primary),
  // outline = finish.
  import { computed, getCurrentInstance, ref } from 'vue';
  import { breakpointsTailwind, useBreakpoints, useNow } from '@vueuse/core';
  import { IOrder } from '@/interfaces/IOrder';
  import { ITable } from '@/interfaces/ITable';
  import type { IPosTableReservationHint } from '@/interfaces/IPosTableView';
  import { getPosTableOccupiedSince } from '@/domain/reservations/pos-table-hints';
  import { deriveOpenTableSession } from '@/domain/pos/table-session-derivation';
  import OrdersService from '@/api/services/orders.service';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import PosV2CoversModal, {
    IPosV2CoversResult,
  } from '@/components/pos-v2/modals/PosV2CoversModal.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';

  const props = defineProps<{
    table: ITable | null;
    tableOrders: IOrder[];
    reservation?: IPosTableReservationHint;
    search: string;
    selectedCount: number;
    totalCount: number;
    hasOrders: boolean;
    canCreateOrder: boolean;
    closing: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'update:search', value: string): void;
    (e: 'select-all'): void;
    (e: 'clear-selection'): void;
    (e: 'new-order'): void;
    (e: 'close-table'): void;
    (e: 'back'): void;
  }>();

  const { smaller } = useBreakpoints(breakpointsTailwind);
  const isCompact = smaller('lg');

  const modals = useModal();
  const ui = useUi();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const hasSelection = computed(() => props.selectedCount > 0);

  const now = useNow({ interval: 30_000 });

  const openSinceText = computed(() => {
    const oldest = getPosTableOccupiedSince({
      orders: props.tableOrders,
      reservation: props.reservation,
    });
    if (!oldest) return '';

    const elapsed = Math.max(0, now.value.getTime() - oldest);
    const minutes = Math.floor(elapsed / 60000);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    return `${minutes}m`;
  });

  //#region Session covers chip

  const openSession = computed(() => deriveOpenTableSession(props.tableOrders));

  const coversLabel = computed(() => {
    const session = openSession.value;
    if (!session)
      return props.reservation?.status === 'seated'
        ? String(props.reservation.partySize)
        : null;
    if (session.covers != null) return String(session.covers);
    // Unconfirmed customer suggestion: nudge staff with "N?" (amber chip).
    if (session.coversSuggested != null) return `${session.coversSuggested}?`;
    return i18nT('posV2.covers');
  });

  /** Customer suggested a party size but staff hasn't confirmed it yet. */
  const coversPendingSuggestion = computed(() => {
    const session = openSession.value;
    return (
      !!session && session.covers == null && session.coversSuggested != null
    );
  });

  const savingCovers = ref(false);

  async function editCovers() {
    const session = openSession.value;
    if (!session || savingCovers.value) return;

    const result = await modals.showDynamicDialog<IPosV2CoversResult | null>(
      PosV2CoversModal,
      {
        tableName: props.table?.name ?? null,
        initial: session.covers,
        suggested: session.covers == null ? session.coversSuggested : null,
        skipLabel: i18nT('posV2.removeCovers'),
      },
      'pos-covers',
      { width: 420 }
    );

    if (!result || result.covers === session.covers) return;

    savingCovers.value = true;
    try {
      const updated = await OrdersService.patchTableSessionCovers(
        session.id,
        result.covers
      );

      // Optimistic mirror on the store orders' embeds (same reactive objects)
      // — the server-side notifyOrderUpdate refresh confirms it via socket.
      // The server stamp too, so the freshest-embed derivation keeps
      // preferring these values over pre-edit WS stragglers.
      for (const order of props.tableOrders) {
        if (order.tableSession?.id === session.id) {
          order.tableSession.covers = updated.covers;
          if (updated.updatedAt) {
            order.tableSession.updatedAt = updated.updatedAt;
          }
        }
      }
    } catch (error) {
      ui.serverError(error, i18nT('posV2.coversSaveError'));
    } finally {
      savingCovers.value = false;
    }
  }

  //#endregion
</script>

<template>
  <div class="pos-v2-table-header">
    <div class="header-left">
      <PosUiButton
        icon="arrow-left"
        color="neutral"
        variant="ghost"
        size="md"
        class="back-button"
        @click="emit('back')"
      />
      <div v-if="table" class="identity">
        <h2 class="table-title">
          {{ $t('posV2.tableWithName', { name: table.name }) }}
        </h2>
        <div class="identity-meta">
          <span v-if="openSinceText" class="meta-item">
            <PosUiIcon name="clock-outline" class="meta-icon" />
            {{ $t('posV2.openSince', { time: openSinceText }) }}
          </span>
          <span v-if="tableOrders.length" class="meta-item">
            {{ tableOrders.length }}
            {{
              tableOrders.length === 1
                ? $t('posV2.orderWord')
                : $t('posV2.ordersWord')
            }}
          </span>
          <button
            v-if="coversLabel"
            type="button"
            class="covers-chip"
            :class="{ 'covers-chip--suggested': coversPendingSuggestion }"
            :disabled="savingCovers || !openSession"
            :title="
              coversPendingSuggestion
                ? $t('posV2.coversConfirmHint')
                : $t('posV2.covers')
            "
            @click="editCovers"
          >
            <PosUiIcon name="account-group" class="covers-icon" />
            <span class="covers-value">{{ coversLabel }}</span>
          </button>
        </div>
      </div>
    </div>

    <div class="header-search">
      <PosUiInput
        :value="search"
        icon="magnify"
        :placeholder="$t('posV2.searchOrdersPlaceholder')"
        size="md"
        clearable
        class="search-input"
        @input="(v) => emit('update:search', v)"
      />
    </div>

    <div class="header-right">
      <PosUiButton
        v-if="!hasSelection"
        color="neutral"
        variant="ghost"
        size="md"
        icon="checkbox-multiple-marked-outline"
        :disabled="totalCount === 0"
        class="action-btn"
        @click="emit('select-all')"
      >
        <span v-if="!isCompact">{{ $t('posV2.selectAll') }}</span>
      </PosUiButton>
      <PosUiButton
        v-else
        color="neutral"
        variant="ghost"
        size="md"
        icon="checkbox-blank-off-outline"
        class="action-btn"
        @click="emit('clear-selection')"
      >
        <span v-if="!isCompact">
          {{ $t('posV2.clearSelectionCount', { count: selectedCount }) }}
        </span>
      </PosUiButton>

      <PosUiButton
        color="neutral"
        variant="outline"
        size="md"
        icon="exit-run"
        :disabled="!hasOrders || closing"
        class="action-btn"
        @click="emit('close-table')"
      >
        <span v-if="!isCompact">{{ $t('posV2.finishTable') }}</span>
      </PosUiButton>

      <PosUiButton
        color="primary"
        variant="solid"
        size="md"
        icon="plus"
        :disabled="!canCreateOrder"
        class="action-btn"
        @click="emit('new-order')"
      >
        <span v-if="!isCompact">{{ $t('posV2.order') }}</span>
      </PosUiButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-table-header {
    @apply tw-sticky tw-top-0 tw-z-10 tw-flex tw-flex-none tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-2 tw-border-b tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2;

    .header-left {
      @apply tw-flex tw-min-w-0 tw-flex-none tw-items-center tw-gap-1;

      .back-button {
        @apply tw-flex-none;
      }

      .identity {
        @apply tw-flex tw-min-w-0 tw-flex-col;

        .table-title {
          @apply tw-truncate tw-text-base tw-font-bold tw-leading-tight tw-text-neutral-900;
        }

        .identity-meta {
          @apply tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-text-xs tw-text-neutral-500;

          .meta-item {
            @apply tw-flex tw-items-center tw-gap-1;

            .meta-icon {
              @apply tw-text-sm tw-text-neutral-400;
            }
          }

          .covers-chip {
            // Icon margin instead of flex gap: unsupported on old POS devices
            // (Chrome <84).
            @apply tw-flex tw-items-center tw-rounded-md tw-border tw-border-neutral-200 tw-bg-white tw-px-1.5 tw-py-0.5 tw-text-xs tw-text-neutral-600 tw-transition-colors hover:tw-bg-neutral-100 disabled:tw-opacity-50;

            .covers-icon {
              @apply tw-mr-1 tw-text-sm tw-text-neutral-400;
            }

            .covers-value {
              @apply tw-font-medium;
            }

            // Amber nudge: the customer suggested covers, awaiting staff
            // confirmation (same palette as the modal's suggestion hint).
            &--suggested {
              @apply tw-border-amber-300 tw-bg-amber-50 tw-text-amber-800 hover:tw-bg-amber-100;

              .covers-icon {
                @apply tw-text-amber-500;
              }
            }
          }
        }
      }
    }

    .header-search {
      @apply tw-min-w-[140px] tw-max-w-sm tw-flex-1;

      .search-input {
        @apply tw-w-full;
      }
    }

    .header-right {
      @apply tw-ml-auto tw-flex tw-flex-none tw-items-center tw-gap-2 max-sm:tw-w-full max-sm:tw-justify-end;

      .action-btn {
        @apply tw-whitespace-nowrap;
      }
    }
  }
</style>
